/**
 * The physics behind the molecular field.
 *
 * Pure functions, deliberately: the drawing lives in ParticleField.jsx, but the
 * motion is the part that can be subtly wrong in a way nobody notices for ten
 * minutes, so it lives here where it can be tested without a browser.
 *
 * Two failure modes this exists to prevent, both of which look fine for the
 * first minute and wrong after twenty:
 *
 *   - Adding an attraction force to velocity every frame with nothing removing
 *     it means particles accelerate for as long as the tab is open. Hence the
 *     damping and the speed clamp.
 *   - Damping plus a resting cursor turns the pointer into a drain: every
 *     particle falls into it, stops, and the rest of the screen empties out.
 *     Hence the minimum speed — the field never fully settles.
 */

export const LINK = 140;        // px between two nodes for a bond to be drawn
export const REACH = 200;       // px within which the cursor takes hold
export const PULL = 0.00006;    // how hard the cursor pulls
export const DAMPING = 0.995;   // bleeds off the energy the cursor adds
export const MAX_SPEED = 0.75;  // nothing ever moves faster than this
export const MIN_SPEED = 0.05;  // ...and nothing ever fully stops

/** Density scales with area — a laptop shouldn't do a monitor's work. */
export const densityFor = (width, height) =>
  Math.min(Math.round((width * height) / 19_000), 90);

export function createParticle(width, height, random = Math.random) {
  return {
    x: random() * width,
    y: random() * height,
    vx: (random() - 0.5) * 0.32,
    vy: (random() - 0.5) * 0.32,
    r: random() * 1.7 + 0.7,
  };
}

/**
 * Advance every particle one frame.
 *
 * `mouse` is `{x, y}` or `{x: null}` when the pointer has left the window.
 * Mutates in place — this runs sixty times a second and allocating a new array
 * each frame is how you get sawtooth garbage collection.
 */
export function stepParticles(particles, width, height, mouse) {
  for (const p of particles) {
    if (mouse && mouse.x !== null && mouse.y !== null) {
      const dx = mouse.x - p.x;
      const dy = mouse.y - p.y;
      const dist = Math.hypot(dx, dy);
      if (dist < REACH && dist > 0) {
        p.vx += dx * PULL;
        p.vy += dy * PULL;
      }
    }

    // Without these two, the cursor's pull accumulates forever.
    p.vx *= DAMPING;
    p.vy *= DAMPING;

    const speed = Math.hypot(p.vx, p.vy);
    if (speed > MAX_SPEED) {
      p.vx = (p.vx / speed) * MAX_SPEED;
      p.vy = (p.vy / speed) * MAX_SPEED;
    } else if (speed < MIN_SPEED) {
      // Keep it drifting. Without this the cursor is a drain: everything falls
      // in, damping kills the velocity, and the field collapses to a blob
      // around wherever the pointer was last left.
      if (speed > 1e-6) {
        p.vx = (p.vx / speed) * MIN_SPEED;
        p.vy = (p.vy / speed) * MIN_SPEED;
      } else {
        const angle = p.r * 1000 % (Math.PI * 2);   // stable per particle
        p.vx = Math.cos(angle) * MIN_SPEED;
        p.vy = Math.sin(angle) * MIN_SPEED;
      }
    }

    p.x += p.vx;
    p.y += p.vy;

    // Bounce, and put the node back inside. A particle left outside the bounds
    // flips its velocity every frame and shivers against the edge.
    if (p.x < 0) { p.x = 0; p.vx = Math.abs(p.vx); }
    else if (p.x > width) { p.x = width; p.vx = -Math.abs(p.vx); }
    if (p.y < 0) { p.y = 0; p.vy = Math.abs(p.vy); }
    else if (p.y > height) { p.y = height; p.vy = -Math.abs(p.vy); }
  }
  return particles;
}

/** Node pairs close enough to bond, with the opacity each bond is drawn at. */
export function bonds(particles, limit = LINK) {
  const out = [];
  for (let i = 0; i < particles.length; i++) {
    const a = particles[i];
    for (let j = i + 1; j < particles.length; j++) {
      const b = particles[j];
      const dx = a.x - b.x;
      const dy = a.y - b.y;
      if (Math.abs(dx) > limit || Math.abs(dy) > limit) continue;   // cheap reject
      const dist = Math.hypot(dx, dy);
      if (dist > limit) continue;
      out.push({ a, b, alpha: (1 - dist / limit) * 0.17 });
    }
  }
  return out;
}

/** Nodes close enough to bond to the cursor. */
export function cursorBonds(particles, mouse) {
  if (!mouse || mouse.x === null || mouse.y === null) return [];
  const out = [];
  for (const p of particles) {
    const dist = Math.hypot(p.x - mouse.x, p.y - mouse.y);
    if (dist > REACH) continue;
    out.push({ p, alpha: (1 - dist / REACH) * 0.42 });
  }
  return out;
}
